import React, { useState, useMemo, useEffect } from 'react';
import { Map, Filter, ImageOff } from 'lucide-react';
import { SectionHeader, Card, MetricTile, EmptyState, CHART_COLORS } from './shared';
import { getDataDragonVersion } from '../../lib/champions';

// Map size from Riot API timeline coordinates (always 14870)
const MAP_SIZE = 14870;

const TYPE_COLORS = {
  investment: '#34d399',
  position: '#fbbf24',
  greed: '#fb7185'
};

// SVG fallback dessiné si toutes les images externes échouent
function MapFallbackSVG() {
  return (
    <svg viewBox={`0 0 ${MAP_SIZE} ${MAP_SIZE}`} className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid meet">
      {/* Background sombre type LoL */}
      <rect width={MAP_SIZE} height={MAP_SIZE} fill="#0d1f1a" />

      {/* River diagonal */}
      <polygon
        points={`0,2000 2000,0 ${MAP_SIZE},${MAP_SIZE - 2000} ${MAP_SIZE - 2000},${MAP_SIZE}`}
        fill="#1e3a5f"
        opacity="0.4"
      />

      {/* Top lane (en L inversé : haut puis gauche) */}
      <rect x="500" y="500" width="1200" height={MAP_SIZE - 4000} fill="#2d3a2d" opacity="0.6" />
      <rect x="500" y="500" width={MAP_SIZE - 4000} height="1200" fill="#2d3a2d" opacity="0.6" />

      {/* Bot lane (en L : bas puis droite) */}
      <rect x="3500" y={MAP_SIZE - 1700} width={MAP_SIZE - 4000} height="1200" fill="#2d3a2d" opacity="0.6" />
      <rect x={MAP_SIZE - 1700} y="3500" width="1200" height={MAP_SIZE - 4000} fill="#2d3a2d" opacity="0.6" />

      {/* Mid lane (diagonale centrale) */}
      <polygon
        points={`3000,${MAP_SIZE - 3000} ${MAP_SIZE - 3000},3000 ${MAP_SIZE - 2200},3800 3800,${MAP_SIZE - 2200}`}
        fill="#2d3a2d"
        opacity="0.6"
      />

      {/* Base alliée (bas-gauche) */}
      <circle cx="2200" cy={MAP_SIZE - 2200} r="2000" fill="#1e40af" opacity="0.3" />
      <circle cx="2200" cy={MAP_SIZE - 2200} r="800" fill="#3b82f6" opacity="0.4" />

      {/* Base ennemie (haut-droite) */}
      <circle cx={MAP_SIZE - 2200} cy="2200" r="2000" fill="#991b1b" opacity="0.3" />
      <circle cx={MAP_SIZE - 2200} cy="2200" r="800" fill="#dc2626" opacity="0.4" />

      {/* Labels */}
      <text x="1500" y="3500" fill="#52525b" fontSize="500" textAnchor="middle" fontWeight="bold" opacity="0.5">TOP</text>
      <text x={MAP_SIZE / 2} y={MAP_SIZE / 2 + 200} fill="#52525b" fontSize="500" textAnchor="middle" fontWeight="bold" opacity="0.5">MID</text>
      <text x={MAP_SIZE - 1500} y={MAP_SIZE - 3500} fill="#52525b" fontSize="500" textAnchor="middle" fontWeight="bold" opacity="0.5">BOT</text>

      {/* Petit label pour identifier que c'est le fallback */}
      <text x="500" y={MAP_SIZE - 500} fill="#3f3f46" fontSize="300" opacity="0.5">Schematic SR</text>
    </svg>
  );
}

export default function HeatmapSection({ games, champions }) {
  const [filterType, setFilterType] = useState('all');
  const [filterChampion, setFilterChampion] = useState('all');
  const [filterPhase, setFilterPhase] = useState('all');
  const [filterResult, setFilterResult] = useState('all');
  const [filterSide, setFilterSide] = useState('all'); // V6.3 : 'all' | 'blue' | 'red' | 'aligned'
  const [imgFailed, setImgFailed] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);
  const [version, setVersion] = useState('14.1.1'); // fallback

  useEffect(() => {
    getDataDragonVersion().then(v => v && setVersion(v));
  }, []);

  // Liste d'URLs à essayer dans l'ordre — la première qui charge gagne
  const imgUrls = [
    `https://ddragon.leagueoflegends.com/cdn/${version}/img/map/map11.png`,
    `https://raw.communitydragon.org/latest/plugins/rcp-fe-lol-static-assets/global/default/images/map.summoners-rift.png`,
    `https://raw.communitydragon.org/latest/game/levels/map11/2dlevelminimap.png`,
  ];

  // Récupérer toutes les morts avec coordonnées
  const allDeathsWithCoords = useMemo(() => {
    const deaths = [];
    games.forEach(g => {
      (g.deathDetails || []).forEach(d => {
        if (d.position && typeof d.position.x === 'number' && typeof d.position.y === 'number') {
          deaths.push({
            ...d,
            game: g,
            x: d.position.x,
            y: d.position.y,
            side: g.mySide || 'unknown' // V6.3
          });
        }
      });
    });
    return deaths;
  }, [games]);

  // Filtres appliqués
  const filteredDeaths = useMemo(() => {
    return allDeathsWithCoords.filter(d => {
      if (filterType !== 'all' && d.type !== filterType) return false;
      if (filterChampion !== 'all' && d.game.champion !== filterChampion) return false;
      if (filterResult !== 'all' && d.game.result !== filterResult) return false;
      if (filterPhase !== 'all') {
        const min = +d.minute || 0;
        if (filterPhase === 'early' && min >= 14) return false;
        if (filterPhase === 'mid' && (min < 14 || min >= 25)) return false;
        if (filterPhase === 'late' && min < 25) return false;
      }
      // V6.3 : filtre side
      if (filterSide === 'blue' && d.side !== 'blue') return false;
      if (filterSide === 'red' && d.side !== 'red') return false;
      // Mode 'aligned' : on garde toutes les morts mais on transforme les coordonnées Red en miroir
      return true;
    }).map(d => {
      // V6.4 FIX : si mode 'aligned' et mort Red side → swap x/y (réflexion sur la diagonale Blue-base ↔ Red-base)
      // L'ancien code faisait MAP_SIZE - x et MAP_SIZE - y (miroir total) qui projetait
      // bot Red vers top Blue. Le bon miroir pour LoL est un swap x↔y.
      if (filterSide === 'aligned' && d.side === 'red') {
        return {
          ...d,
          x: d.y,
          y: d.x
        };
      }
      return d;
    });
  }, [allDeathsWithCoords, filterType, filterChampion, filterPhase, filterResult, filterSide]);

  // V6.3 : Stats par side pour insight
  const sideStats = useMemo(() => {
    const blueGames = games.filter(g => g.mySide === 'blue');
    const redGames = games.filter(g => g.mySide === 'red');
    const blueDeaths = allDeathsWithCoords.filter(d => d.side === 'blue').length;
    const redDeaths = allDeathsWithCoords.filter(d => d.side === 'red').length;
    return {
      blueGames: blueGames.length,
      redGames: redGames.length,
      blueDeaths,
      redDeaths,
      blueWR: blueGames.length > 0 ? Math.round(blueGames.filter(g => g.result === 'W').length / blueGames.length * 100) : 0,
      redWR: redGames.length > 0 ? Math.round(redGames.filter(g => g.result === 'W').length / redGames.length * 100) : 0,
      blueDPG: blueGames.length > 0 ? +(blueDeaths / blueGames.length).toFixed(1) : 0,
      redDPG: redGames.length > 0 ? +(redDeaths / redGames.length).toFixed(1) : 0,
    };
  }, [games, allDeathsWithCoords]);

  const playedChampions = useMemo(() => {
    const set = new Set(games.map(g => g.champion));
    return Array.from(set).sort();
  }, [games]);

  if (allDeathsWithCoords.length === 0) {
    return (
      <EmptyState
        message="Pas de coordonnées de morts disponibles"
        sub="Importe des games via Riot API pour avoir les coordonnées (les games saisies manuellement n'en ont pas)"
      />
    );
  }

  // Stats agrégées
  const totalDeaths = filteredDeaths.length;
  const byType = {
    investment: filteredDeaths.filter(d => d.type === 'investment').length,
    position: filteredDeaths.filter(d => d.type === 'position').length,
    greed: filteredDeaths.filter(d => d.type === 'greed').length
  };

  const handleImgError = () => {
    if (imgIdx < imgUrls.length - 1) {
      setImgIdx(imgIdx + 1);
    } else {
      setImgFailed(true);
    }
  };

  return (
    <div className="space-y-6">
      <SectionHeader title="Heatmap des Morts" subtitle={`${totalDeaths} morts géolocalisées sur ${allDeathsWithCoords.length} disponibles`} icon={Map} />

      {/* Filtres */}
      <Card>
        <div className="flex items-center gap-2 mb-3">
          <Filter size={12} className="text-cyan-400" />
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-400 font-semibold">Filtres</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <div>
            <div className="text-[10px] text-zinc-600 font-mono mb-1">TYPE</div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-800 text-white text-xs p-2 focus:border-cyan-400 focus:outline-none"
            >
              <option value="all">Tous types</option>
              <option value="investment">Investissement</option>
              <option value="position">Position</option>
              <option value="greed">Greed</option>
            </select>
          </div>
          <div>
            <div className="text-[10px] text-zinc-600 font-mono mb-1">CHAMPION</div>
            <select
              value={filterChampion}
              onChange={(e) => setFilterChampion(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-800 text-white text-xs p-2 focus:border-cyan-400 focus:outline-none"
            >
              <option value="all">Tous champions</option>
              {playedChampions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] text-zinc-600 font-mono mb-1">PHASE</div>
            <select
              value={filterPhase}
              onChange={(e) => setFilterPhase(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-800 text-white text-xs p-2 focus:border-cyan-400 focus:outline-none"
            >
              <option value="all">Toutes phases</option>
              <option value="early">Early (0-14min)</option>
              <option value="mid">Mid (14-25min)</option>
              <option value="late">Late (25min+)</option>
            </select>
          </div>
          <div>
            <div className="text-[10px] text-zinc-600 font-mono mb-1">RÉSULTAT</div>
            <select
              value={filterResult}
              onChange={(e) => setFilterResult(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-800 text-white text-xs p-2 focus:border-cyan-400 focus:outline-none"
            >
              <option value="all">W + L</option>
              <option value="W">Victoires</option>
              <option value="L">Défaites</option>
            </select>
          </div>
          <div>
            <div className="text-[10px] text-zinc-600 font-mono mb-1">SIDE</div>
            <select
              value={filterSide}
              onChange={(e) => setFilterSide(e.target.value)}
              className="w-full bg-zinc-900 border border-zinc-800 text-white text-xs p-2 focus:border-cyan-400 focus:outline-none"
            >
              <option value="all">Both sides</option>
              <option value="blue">Blue side</option>
              <option value="red">Red side</option>
              <option value="aligned">Alignées (miroir)</option>
            </select>
          </div>
        </div>
        {filterSide === 'aligned' && (
          <div className="text-[10px] text-cyan-400/70 font-mono mt-2 italic">
            Mode aligné : les morts Red side sont mappées en miroir pour s'aligner sur Blue. Ta base est toujours en bas-gauche.
          </div>
        )}
      </Card>

      {/* V6.3 : Comparaison Blue vs Red */}
      {sideStats.blueGames > 0 && sideStats.redGames > 0 && (
        <Card>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-400 font-semibold mb-3">Comparaison Blue vs Red</div>
          <div className="grid grid-cols-2 gap-3">
            <div className="border-l-4 border-blue-400 bg-blue-400/5 p-3">
              <div className="text-[10px] tracking-[0.3em] text-blue-400 uppercase font-bold mb-2">Blue Side</div>
              <div className="grid grid-cols-3 gap-2 text-xs font-mono">
                <div>
                  <div className="text-[9px] text-zinc-500 uppercase tracking-wider">Games</div>
                  <div className="text-white font-bold">{sideStats.blueGames}</div>
                </div>
                <div>
                  <div className="text-[9px] text-zinc-500 uppercase tracking-wider">WR</div>
                  <div className={`font-bold ${sideStats.blueWR >= 54 ? 'text-cyan-400' : 'text-rose-400'}`}>{sideStats.blueWR}%</div>
                </div>
                <div>
                  <div className="text-[9px] text-zinc-500 uppercase tracking-wider">Morts/G</div>
                  <div className={`font-bold ${sideStats.blueDPG <= 4 ? 'text-cyan-400' : 'text-rose-400'}`}>{sideStats.blueDPG}</div>
                </div>
              </div>
            </div>
            <div className="border-l-4 border-rose-500 bg-rose-500/5 p-3">
              <div className="text-[10px] tracking-[0.3em] text-rose-400 uppercase font-bold mb-2">Red Side</div>
              <div className="grid grid-cols-3 gap-2 text-xs font-mono">
                <div>
                  <div className="text-[9px] text-zinc-500 uppercase tracking-wider">Games</div>
                  <div className="text-white font-bold">{sideStats.redGames}</div>
                </div>
                <div>
                  <div className="text-[9px] text-zinc-500 uppercase tracking-wider">WR</div>
                  <div className={`font-bold ${sideStats.redWR >= 54 ? 'text-cyan-400' : 'text-rose-400'}`}>{sideStats.redWR}%</div>
                </div>
                <div>
                  <div className="text-[9px] text-zinc-500 uppercase tracking-wider">Morts/G</div>
                  <div className={`font-bold ${sideStats.redDPG <= 4 ? 'text-cyan-400' : 'text-rose-400'}`}>{sideStats.redDPG}</div>
                </div>
              </div>
            </div>
          </div>
          {Math.abs(sideStats.blueWR - sideStats.redWR) >= 15 && (
            <div className="mt-3 text-[11px] text-amber-400 italic font-mono">
              💡 Écart significatif entre les sides : tu performes mieux côté <strong>{sideStats.blueWR > sideStats.redWR ? 'Blue' : 'Red'}</strong> ({Math.abs(sideStats.blueWR - sideStats.redWR)}% d'écart). À garder à l'esprit.
            </div>
          )}
        </Card>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricTile label="Total filtré" value={totalDeaths} color="white" />
        <MetricTile label="Investment" value={byType.investment} color="emerald" />
        <MetricTile label="Position" value={byType.position} color="amber" />
        <MetricTile label="Greed" value={byType.greed} color="rose" />
      </div>

      {/* La heatmap (Map officielle de SR avec dots) */}
      <Card>
        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-400 font-semibold mb-3 flex items-center justify-between">
          <span>Summoner's Rift</span>
          {imgFailed && (
            <span className="text-[10px] text-amber-400 font-mono flex items-center gap-1 normal-case tracking-normal">
              <ImageOff size={10} /> Map officielle indisponible, fallback schématique
            </span>
          )}
        </div>
        <div className="relative w-full max-w-[600px] mx-auto bg-zinc-950" style={{ aspectRatio: '1/1' }}>
          {/* Image officielle de Summoner's Rift OU fallback SVG */}
          {!imgFailed ? (
            <img
              key={imgIdx}
              src={imgUrls[imgIdx]}
              alt="Summoner's Rift"
              className="absolute inset-0 w-full h-full object-cover"
              onError={handleImgError}
            />
          ) : (
            <MapFallbackSVG />
          )}

          {/* Overlay sombre pour mieux voir les dots */}
          <div className="absolute inset-0 bg-black/40 pointer-events-none" />

          {/* Death dots en SVG par-dessus */}
          <svg viewBox={`0 0 ${MAP_SIZE} ${MAP_SIZE}`} className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid meet">
            {filteredDeaths.map((d, i) => (
              <g key={i}>
                {/* Glow extérieur */}
                <circle
                  cx={d.x}
                  cy={MAP_SIZE - d.y}
                  r="320"
                  fill={TYPE_COLORS[d.type] || '#71717a'}
                  opacity="0.2"
                />
                {/* Dot principal */}
                <circle
                  cx={d.x}
                  cy={MAP_SIZE - d.y}
                  r="180"
                  fill={TYPE_COLORS[d.type] || '#71717a'}
                  opacity="0.9"
                  stroke="#000"
                  strokeWidth="40"
                >
                  <title>
                    {d.game.champion} vs {d.game.enemyAdc} · {d.minute}min · {d.type} · {d.context}
                  </title>
                </circle>
              </g>
            ))}
          </svg>

          {/* Légende */}
          <div className="absolute bottom-2 left-2 right-2 bg-zinc-950/90 border border-zinc-800 px-3 py-2 flex items-center justify-between text-[10px] font-mono">
            <div className="flex items-center gap-3">
              {[
                { type: 'investment', label: 'Invest', color: TYPE_COLORS.investment },
                { type: 'position', label: 'Position', color: TYPE_COLORS.position },
                { type: 'greed', label: 'Greed', color: TYPE_COLORS.greed }
              ].map(l => (
                <div key={l.type} className="flex items-center gap-1.5">
                  <div className="w-2 h-2 rounded-full" style={{ background: l.color }} />
                  <span className="text-zinc-400">{l.label}</span>
                </div>
              ))}
            </div>
            <div className="text-zinc-500">{totalDeaths} morts</div>
          </div>
        </div>
        <div className="text-[10px] text-zinc-500 font-mono mt-3 text-center">
          Survole un point pour voir le détail. Base alliée en bas-gauche, ennemie en haut-droite.
        </div>
      </Card>
    </div>
  );
}
